export const cleaningServicesData = {
  industry: "Cleaning Services",
  slug: "cleaning-services",
  
  seo: {
    title: "Intake Forms for Cleaning Services | HeyIntake",
    description: "Quote cleaning jobs accurately with AI-powered intake forms. Capture home size, cleaning frequency, special requests, and pet information before sending estimates.",
    keywords: [
      "cleaning service intake form",
      "house cleaning quote form",
      "maid service inquiry form",
      "cleaning business lead form",
      "residential cleaning intake",
      "commercial cleaning questionnaire"
    ]
  },

  heroTitle: "Intake Forms for Cleaning Services",
  heroSubtitle: "Get accurate job details before you quote. Know the square footage, pet situation, and special requests upfront.",
  heroDescription: "Stop sending quotes blind. HeyIntake helps you capture home size, number of rooms, cleaning frequency, pet information, and access details so your estimates are accurate and your teams arrive prepared.",
  
  whyAIScoring: {
    title: "Why Cleaning Businesses Need Smart Intake",
    intro: "Accurate quotes are the foundation of a profitable cleaning business. Underquote and you lose money. Overquote and you lose the job.",
    paragraphs: [
      "Most cleaning businesses quote based on a quick phone call or vague online inquiry. 'I need a cleaning' tells you nothing about square footage, number of bathrooms, pet hair situation, or whether they want a deep clean or maintenance service. You end up guessing — and often guessing wrong.",
      "HeyIntake captures everything you need for accurate quoting before you respond. Home size, number of rooms, last cleaning date, pets, special requests, and their budget range. Our AI even flags potential issues like hoarding situations or unrealistic expectations.",
      "For residential cleaners, commercial cleaning companies, and maid services, this means accurate quotes that protect your margins, prepared teams that don't get surprised on-site, and a professional image that wins bids."
    ]
  },

  painPoints: [
    {
      title: "Inaccurate Quotes",
      description: "Quoting based on incomplete information, then arriving to find a much bigger job than expected."
    },
    {
      title: "Surprise Conditions",
      description: "Teams arriving to find pet hair everywhere, hoarding situations, or post-construction messes they weren't prepared for."
    },
    {
      title: "Scheduling Chaos",
      description: "Back-and-forth messages trying to confirm dates, times, and access instructions."
    },
    {
      title: "Unrealistic Expectations",
      description: "Clients expecting deep-clean results at maintenance-clean prices."
    },
    {
      title: "Access Issues",
      description: "Teams arriving to locked doors, no parking, or apartment buildings with no access codes."
    },
    {
      title: "Price Shoppers",
      description: "Wasting time quoting leads who are just collecting estimates with no intent to book."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Location",
      fields: [
        "Full name",
        "Phone and email",
        "Property address",
        "Property type (house, apartment, condo)",
        "How did you hear about us?"
      ]
    },
    {
      category: "Property Details",
      fields: [
        "Approximate square footage",
        "Number of bedrooms",
        "Number of bathrooms",
        "Number of floors",
        "Garage/basement included?"
      ]
    },
    {
      category: "Cleaning Requirements",
      fields: [
        "Service type (standard, deep clean, move-out)",
        "Desired frequency (one-time, weekly, bi-weekly)",
        "Last professional cleaning date",
        "Priority areas",
        "Any areas to avoid?"
      ]
    },
    {
      category: "Special Considerations",
      fields: [
        "Pets? (type and number)",
        "Anyone home during cleaning?",
        "Access instructions (lockbox, doorman)",
        "Parking situation",
        "Cleaning supplies provided or bring own?"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Cleaning Service Intake Form",
    description: "Here's what a quote-ready intake form looks like:",
    fields: [
      { label: "Type of cleaning needed", type: "select", options: ["Standard cleaning", "Deep clean", "Move-in/Move-out", "Post-construction", "One-time clean"] },
      { label: "Approximate square footage", type: "select", options: ["Under 1,000 sq ft", "1,000-2,000 sq ft", "2,000-3,000 sq ft", "3,000+ sq ft"] },
      { label: "Number of bedrooms", type: "select", options: ["Studio", "1", "2", "3", "4+"] },
      { label: "Number of bathrooms", type: "select", options: ["1", "2", "3", "4+"] },
      { label: "Do you have pets?", type: "select", options: ["No pets", "Cat(s)", "Dog(s)", "Multiple pets", "Other"] },
      { label: "How often do you need cleaning?", type: "select", options: ["One-time", "Weekly", "Bi-weekly", "Monthly"] },
      { label: "When was the last professional clean?", type: "select", options: ["Never", "Within 30 days", "1-3 months ago", "6+ months ago"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Cleaning Services",
    subtitle: "From inquiry to accurate quote in three steps",
    steps: [
      {
        title: "Client Fills Out Details",
        description: "Potential clients provide property details, cleaning needs, and access information."
      },
      {
        title: "AI Calculates & Flags",
        description: "Our AI estimates job complexity, flags potential issues, and scores lead quality."
      },
      {
        title: "Quote with Confidence",
        description: "Send accurate quotes based on complete information. Arrive prepared, not surprised."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Lead Qualification for Cleaners",
    description: "HeyIntake's AI helps you identify profitable jobs and flag potential problem clients before you quote.",
    factors: [
      "Job complexity based on size and condition",
      "Budget alignment with your pricing",
      "Booking intent vs. just price shopping",
      "Red flags (hoarding, unrealistic expectations)",
      "Recurring potential vs. one-time"
    ],
    exampleLead: {
      name: "Sarah Mitchell",
      context: "2,500 sq ft • Bi-weekly • 2 dogs",
      summary: "3BR/2BA home, bi-weekly service requested. Two medium dogs. Last cleaned 1 month ago. Budget aligns with standard pricing. Looking for long-term cleaner.",
      score: 88
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Quote Accurately Every Time",
      description: "Know the full scope before sending estimates. No more surprise big jobs."
    },
    {
      icon: "file",
      title: "Collect Access Details",
      description: "Get lockbox codes, parking info, and entry instructions before the team arrives."
    },
    {
      icon: "clock",
      title: "Screen for Recurring Clients",
      description: "Prioritize inquiries looking for weekly or bi-weekly service over one-time cleans."
    },
    {
      icon: "users",
      title: "Prepare Your Team",
      description: "Teams arrive knowing the pet situation, priority areas, and any special requests."
    },
    {
      icon: "zap",
      title: "Automate Booking Flow",
      description: "From inquiry to confirmed appointment without endless phone tag."
    },
    {
      icon: "chart",
      title: "Track Lead Pipeline",
      description: "See who's inquired, quoted, and converted to recurring client."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" },
    { name: "Intake Forms for Real Estate Agents", path: "/intake-form-for-real-estate-agents" },
    { name: "Intake Forms for Event Planners", path: "/intake-form-for-event-planners" },
    { name: "Intake Forms for Photographers", path: "/intake-form-for-photographers" }
  ],

  ctaText: "Ready to Quote Smarter?"
};
